import React, { useCallback, useEffect, useState } from "react";
import useStickyHeight from "../hooks/useStickyHeight";
import mainLogo from "../assets/images/unum-Insights-logo.svg";
import { useSelector, useDispatch } from "react-redux";
import SubHeader from "./SubHeader";
import Sidebar from "./sidebar/Sidebar";
import { logout } from "./../slices/authSlice";
import { useLocation, useNavigate } from "react-router-dom";
import OrganizationLogo from "./OrganizationLogo";
import { displayLogo } from "../constants/index";
import exportIcon from "../assets/icons/export-icon.svg";
import GTM from "../GTM";
import { useFlags } from "launchdarkly-react-client-sdk";
import InfomationBanner from "./InfomationBanner";
import warning from "../assets/icons/warning-icon.svg";
import InformationBanner from "../assets/icons/displayInformationBanner-icon.svg";
import errorBanner from "../assets/icons/error-banner-icon.svg";
import WarningPage from "../views/preferences/WarningPage";
import { useEventContext } from "../EventContext";
import { setPreferenceChanged } from "../slices/preferenceSlice";
import PreferenceSaveNotification from "../views/preferences/PreferenceSaveNotification";
import HelpSection from "../views/help/HelpSection";
import { getIsDashboardEnabled, matchReportUrl } from "../utils/helpers";
import { setShowHelpSection } from "../slices/helpContentSlice";

function Header({
    showInformationBanner,
    hideInformationBanner,
    showNotification,
    handleClosePreference,
}) {
    const { user, returnUrl } = useSelector((state) => state.auth);
    const [show, setShow] = useState(false);
    const [pageSelected, setPageSelected] = useState();
    const [pageGtmTags, setPageGtmTags] = useState();
    const { isPreferenceChanged } = useSelector(
        (state) => state.preferenceSlice
    );
    const showHelpSection = useSelector(
        (state) => state?.helpContent?.showHelpSection
    );

    const {
        lastSelectedOrganization: organization,
        currentSelectedOrganization,
        totalParentOrganizations,
    } = useSelector((state) => state.organizations);

    const { handleEvent } = useEventContext();
    const location = useLocation();
    const navigate = useNavigate();

    const { config } = useSelector((state) => state.app);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [openHelp, setOpenHelp] = useState(false);
    const toggleRef = React.useRef(null);
    const skipLinkRef = React.useRef(null);
    const headerRef = React.useRef(null);
    const dashboardURl = "/dashboard";

    const getFocusableElements = () => {
        return Array.from(
            document.querySelectorAll(
                'a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
            )
        ).filter((el) => el.offsetParent !== null && !el.disabled);
    };

    const focusSkipLink = () => {
        if (skipLinkRef.current) {
            skipLinkRef.current.style.visibility = "visible";
            skipLinkRef.current.focus();
        }
    };

    const focusPreviousElement = (focusableElements, activeElement) => {
        const index = focusableElements.indexOf(activeElement);
        if (index > 0) {
            focusableElements[index - 1].focus();
        } else if (index === 0) {
            focusableElements[focusableElements.length - 1].focus();
        }
    };

    const handleFirstTabPress = (e) => {
        if (e.key !== "Tab") return;

        const focusableElements = getFocusableElements();
        const lastFocusable = focusableElements[focusableElements.length - 1];
        const activeElement = document.activeElement;
        const scrollToTop = document.getElementById("scroll-to-top");
        const searchElement = document.querySelector('[data-testid="Search"]');

        const isFirstTab = !e.shiftKey && activeElement === document.body;
        const isOnScrollToTop = activeElement === scrollToTop;
        const isLastFocusable =
            activeElement === lastFocusable && activeElement !== scrollToTop;

        if (isFirstTab || isOnScrollToTop) {
            e.preventDefault();
            focusSkipLink();
            return;
        }

        if (isLastFocusable && !e.shiftKey) {
            e.preventDefault();
            if (!scrollToTop.classList.contains("d-none")) {
                scrollToTop.focus();
            } else {
                focusSkipLink();
            }
            return;
        }

        if (e.shiftKey) {
            e.preventDefault();
            if (activeElement === searchElement) {
                focusSkipLink();
            } else {
                focusPreviousElement(focusableElements, activeElement);
            }
        }
    };

    const handleSkipLinkVisibilty = () => {
        if (skipLinkRef.current) {
            skipLinkRef.current.style.visibility = "hidden";
        }
    };

    useEffect(() => {
        window.addEventListener("keydown", handleFirstTabPress);

        return () => {
            window.removeEventListener("keydown", handleFirstTabPress);
        };
    }, []);

    useEffect(() => {
        handleSkipLinkVisibilty();
        window.addEventListener("keydown", handleFirstTabPress);
        return () => {
            window.removeEventListener("keydown", handleFirstTabPress);
        };
    }, [location]);

    useEffect(() => {
        if (showHelpSection === true) {
            setOpenHelp(true);
        }
    }, [showHelpSection]);

    const dispatch = useDispatch();
    const gtm = new GTM(null);

    const closeSidebar = useCallback(() => {
        setSidebarOpen(false);
    }, []);

    const closeHelp = useCallback(() => {
        setOpenHelp(false);
    }, []);

    const handleGtmTags = (label) => {
        gtm.Event(
            "top nav",
            "link",
            "click",
            label,
            "top nav",
            "",
            "navigation_click",
            ""
        );
    };

    const toggleSidebar = () => {
        setSidebarOpen(!sidebarOpen);
    };
    const handleResize = () => {
        if (window.innerWidth > 769) {
            setSidebarOpen(false);
        }
    };
    useEffect(() => {
        window.addEventListener("resize", handleResize);

        return () => {
            window.removeEventListener("resize", handleResize);
        };
    }, []);

    useStickyHeight(headerRef, "--sticky-page-header-height");
    const toggleHelp = () => {
        dispatch(setShowHelpSection(false));
        setOpenHelp(true);
    };
    let url = location.pathname;

    let urlDisplay = url.match(displayLogo);

    const {
        displayWarningBanner,
        displayInformationBanner,
        displayErrorbanner,
        displayDisabilitySnapshotTile,
        displayTrendsBenchmarkingTile,
        displayCliLink,
        displayCostcoDashboard,
    } = useFlags();

    const {
        enabledOrganizationsDisabilitySnapshot,
        enabledOrganizationsTrendsBenchmarking,
        organizations: allOrganizations,
    } = useSelector((state) => state.userDetails) || {};

    const allOrgIds = new Set(
        allOrganizations?.map((org) => org.ORGANIZATIONIDISF) || []
    );

    const getCLILink = () => {
        const isInternal = user?.isUserInternal;

        if (isInternal && displayCliLink?.enableInternalUser) {
            return true;
        }

        if (!isInternal) {
            if (displayCliLink?.enableExternalUser) {
                return true;
            }

            if (displayCliLink?.isfOrgIds?.some((org) => allOrgIds.has(org))) {
                return true;
            }
        }

        return false;
    };

    const isDashboardEnabled = getIsDashboardEnabled({
        displayDisabilitySnapshotTile,
        displayTrendsBenchmarkingTile,
        displayCostcoDashboard,
        user,
        enabledOrganizationsDisabilitySnapshot,
        enabledOrganizationsTrendsBenchmarking,
        allOrganizations,
    });

    const handleInformationBannerClose = () => {
        hideInformationBanner();
    };

    const emitEvent = async () => {
        await handleEvent({ eventType: "ONSAVE_EVENT" });
        closeWarningAndNavigate();
    };

    const closeWarningAndNavigate = () => {
        setShow(false);
        dispatch(setPreferenceChanged(false));
        navigate(pageSelected);
        handleGtmTags(pageGtmTags);
    };

    const getSearchPath = () => {
        if (totalParentOrganizations === 1 && organization?.ORGANIZATIONID) {
            return `/organization/${organization.ORGANIZATIONID}/employees/search`;
        }
        return "/search";
    };

    const handleKeyDown = (e, onClick) => {
        if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick();
        }
    };

    const renderInformationBanner = (
        showInformationBanner,
        displayWarningBanner,
        displayInformationBanner,
        displayErrorbanner,
        handleInformationBannerClose
    ) => {
        return (
            <>
                {showInformationBanner && displayWarningBanner && (
                    <InfomationBanner
                        bannerType="warning"
                        bannerImage={warning}
                        bannerText={displayWarningBanner}
                        onClose={handleInformationBannerClose}
                    />
                )}
                {showInformationBanner && displayInformationBanner && (
                    <InfomationBanner
                        bannerType="information"
                        bannerImage={InformationBanner}
                        bannerText={displayInformationBanner}
                        onClose={handleInformationBannerClose}
                    />
                )}
                {showInformationBanner && displayErrorbanner && (
                    <InfomationBanner
                        bannerType="error"
                        bannerImage={errorBanner}
                        bannerText={displayErrorbanner}
                        onClose={handleInformationBannerClose}
                    />
                )}
            </>
        );
    };

    const renderNavigationLinks = (
        url,
        isPreferenceChanged,
        navigate,
        handleGtmTags,
        setPageSelected,
        setPageGtmTags,
        setShow
    ) => {
        const handleBackButtonClick = (item) => {
            setPageSelected(item);
            setPageGtmTags(item.slice(1));
            if (isPreferenceChanged === true) {
                setShow(true);
            } else {
                setShow(false);
            }
        };

        const handleDashboard = () => {
            setPageSelected(dashboardURl);
            setPageGtmTags(dashboardURl.slice(1));
            if (isPreferenceChanged === true) {
                setShow(true);
            } else {
                setShow(false);
            }
        };

        return (
            <>
                <a
                    id="skip-link"
                    className="skip-link"
                    aria-label="Skip to main content"
                    href="#main-row-content"
                    style={{
                        visibility: "hidden",
                    }}
                    ref={skipLinkRef}
                    tabIndex={0}
                    onBlur={() => handleSkipLinkVisibilty()}
                    onClick={(e) => {
                        e.preventDefault();
                        document.getElementById("main-row-content").focus();
                        handleSkipLinkVisibilty();
                    }}
                    onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                            e.preventDefault();
                            document.getElementById("main-row-content").focus();
                            handleSkipLinkVisibilty();
                        }
                    }}
                >
                    Skip to main content
                </a>
                <a
                    className={
                        (url.includes("/search") ? "text-bold " : "") +
                        "mr-16p clickable"
                    }
                    data-testid="Search"
                    href={getSearchPath()}
                    onClick={(e) => {
                        e.preventDefault();
                        const searchPath = getSearchPath();
                        if (!isPreferenceChanged) {
                            navigate(searchPath);
                            handleGtmTags("search");
                        }
                        handleBackButtonClick(searchPath);
                    }}
                    onKeyDown={(e) =>
                        handleKeyDown(e, () => {
                            const searchPath = getSearchPath();
                            if (!isPreferenceChanged) {
                                navigate(searchPath);
                                handleGtmTags("search");
                            }
                            handleBackButtonClick(searchPath);
                        })
                    }
                >
                    Search
                </a>
                <a
                    className={
                        (url.includes("/reportingcapabilities")
                            ? "text-bold "
                            : "") + "mr-16p clickable"
                    }
                    data-testid="reports"
                    href="/reportingcapabilities"
                    tabIndex={0}
                    onClick={(e) => {
                        e.preventDefault();
                        if (!isPreferenceChanged) {
                            navigate("/reportingcapabilities");
                            handleGtmTags("reports");
                        }
                        handleBackButtonClick("/reportingcapabilities");
                    }}
                    onKeyDown={(e) =>
                        handleKeyDown(e, () => {
                            if (!isPreferenceChanged) {
                                navigate("/reportingcapabilities");
                                handleGtmTags("reports");
                            }
                            handleBackButtonClick("/reportingcapabilities");
                        })
                    }
                >
                    Reports
                </a>
                {isDashboardEnabled && (
                    <a
                        className={
                            (url.includes("/dashboard") ? "text-bold " : "") +
                            "mr-16p clickable"
                        }
                        href={dashboardURl}
                        tabIndex={0}
                        onClick={(e) => {
                            e.preventDefault();
                            if (!isPreferenceChanged) {
                                navigate(dashboardURl);
                                handleGtmTags("dashboard");
                            }
                            handleDashboard();
                        }}
                        onKeyDown={(e) =>
                            handleKeyDown(e, () => {
                                if (!isPreferenceChanged) {
                                    navigate(dashboardURl);
                                    handleGtmTags("dashboard");
                                }
                                handleDashboard();
                            })
                        }
                    >
                        Dashboards
                    </a>
                )}

                <a
                    className={
                        (url.includes("/preferences") ? "text-bold " : "") +
                        "mr-16p clickable"
                    }
                    href="/preferences"
                    tabIndex={0}
                    onClick={(e) => {
                        e.preventDefault();
                        navigate("/preferences");
                        handleGtmTags("preferences");
                    }}
                    onKeyDown={(e) =>
                        handleKeyDown(e, () => {
                            navigate("/preferences");
                            handleGtmTags("preferences");
                        })
                    }
                >
                    Preferences
                </a>
                <div
                    className="mr-16p clickable"
                    role="button"
                    tabIndex={0}
                    ref={toggleRef}
                    onClick={() => {
                        toggleHelp();
                    }}
                >
                    Help
                </div>
            </>
        );
    };

    const renderUserSpecificLinks = (
        user,
        returnUrl,
        config,
        handleGtmTags
    ) => {
        return (
            <>
                {returnUrl && returnUrl.includes("my.unum.com") && (
                    <a
                        className="header-text d-flex mr-16p clickable"
                        data-testid="MyUnum"
                        href={config.REACT_APP_MY_UNUM}
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleGtmTags("myunum for clients")}
                        aria-label="MyUnum For Clients - Opens in a new tab"
                    >
                        MyUnum For Clients
                    </a>
                )}
                {user?.isUserInternal && (
                    <a
                        className="header-text d-flex mr-16p clickable"
                        data-testid="iservices"
                        href={
                            config.REACT_APP_ISERVICES + ":81/LanAuthentication"
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleGtmTags("iservice")}
                        aria-label="iServices - Opens in a new tab"
                    >
                        iServices
                        <img className="ml-4p" src={exportIcon} alt="" />
                    </a>
                )}
                {returnUrl !== null &&
                    !user?.isUserInternal &&
                    !returnUrl?.includes("my.unum.com") && (
                        <a
                            className="header-text d-flex mr-16p clickable"
                            data-testid="iservices"
                            href={
                                config.REACT_APP_ISERVICES +
                                "/_fwWelc/Welcome.aspx"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            onClick={() => handleGtmTags("iservice")}
                            aria-label="iServices - Opens in a new tab"
                        >
                            iServices
                            <img className="ml-4p" src={exportIcon} alt="" />
                        </a>
                    )}
                {getCLILink() && (
                    <a
                        className="header-text d-flex mr-16p clickable"
                        data-testid="CLI"
                        href={
                            user?.isUserInternal
                                ? config.REACT_APP_CLI_INTERNAL
                                : config.REACT_APP_CLI
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={() => handleGtmTags("cli")}
                        aria-label="CLI - Opens in a new tab"
                    >
                        CLI
                        <img className="ml-4p" src={exportIcon} alt="" />
                    </a>
                )}
            </>
        );
    };
    const urls = window.location.pathname;
    const reportUrl = matchReportUrl(urls);
    return (
        <>
            <header id="myHeader" ref={headerRef}>
                {renderInformationBanner(
                    showInformationBanner,
                    displayWarningBanner,
                    displayInformationBanner,
                    displayErrorbanner,
                    handleInformationBannerClose
                )}
                <div
                    className={`align-items-center header ${
                        reportUrl ? "" : "container"
                    }`}
                    style={{
                        paddingLeft: reportUrl ? 48 : 5,
                        paddingRight: reportUrl ? 48 : 5,
                    }}
                >
                    {/* <div
                        className="sidebar-toggle cursor-pointer"
                        role="button"
                        tabIndex={0}
                        onClick={toggleSidebar}
                        ref={toggleRef}
                        title="Menu"
                        onKeyDown={(e) => handleKeyDown(e, toggleSidebar)}
                    >
                        <i
                            className={
                                "fa-solid text-primary-main " +
                                (sidebarOpen ? "fa-times" : "fa-bars")
                            }
                        ></i>
                    </div>
                    <div
                        style={{
                            position: "absolute",
                            width: "1px",
                            height: "1px",
                            padding: "0",
                            margin: "-1px",
                            overflow: "hidden",
                            clip: "rect(0, 0, 0, 0)",
                            whiteSpace: "nowrap",
                            border: "0",
                        }}
                        role="link"
                        aria-label="Skip to main content"
                        onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                document
                                    .getElementById("main-row-content")
                                    .focus();
                            }
                        }}
                        onClick={(e) => {
                            e.preventDefault();
                            document.getElementById("main-row-content").focus();
                        }}
                    >
                        Skip to main content
                    </div> */}
                    <img
                        src={mainLogo}
                        className="logo"
                        alt="unum insights logo"
                    />
                    <div className="d-flex header-text align-items-center nav-bar">
                        {user && (
                            <>
                                {renderNavigationLinks(
                                    url,
                                    isPreferenceChanged,
                                    navigate,
                                    handleGtmTags,
                                    setPageSelected,
                                    setPageGtmTags,
                                    setShow
                                )}
                                {renderUserSpecificLinks(
                                    user,
                                    returnUrl,
                                    config,
                                    handleGtmTags
                                )}
                            </>
                        )}

                        {urlDisplay !== null && (
                            <div>
                                <OrganizationLogo
                                    organizationName={
                                        currentSelectedOrganization?.ORGNIZATIONNAME
                                    }
                                    card={false}
                                    label={false}
                                    height={30}
                                    width={101.99}
                                    div={false}
                                    style={{ marginRight: 16 }}
                                />
                            </div>
                        )}

                        {user && (
                            <button
                                className="signout-button"
                                onClick={() => {
                                    dispatch(logout());
                                    gtm.Event(
                                        "top nav",
                                        "button",
                                        "click",
                                        "sign out",
                                        "top nav",
                                        "",
                                        "sign_out",
                                        ""
                                    );

                                    localStorage.clear();
                                    sessionStorage.clear();
                                }}
                            >
                                Sign out
                            </button>
                        )}
                    </div>
                </div>
                <SubHeader />
                {organization && (
                    <span className="d-none" id="employer-name">
                        {organization?.ORGNIZATIONNAME}
                    </span>
                )}
                {user && (
                    <span className="d-none" id="is-internal-user">
                        {user?.isUserInternal === true ? "Yes" : "No"}
                    </span>
                )}
                {user && (
                    <span className="userid" hidden>
                        {user?.userId}
                    </span>
                )}
                {user && (
                    <span className="user-email" hidden>
                        {user?.userEmail}
                    </span>
                )}
                {user && (
                    <span className="username" hidden>
                        {user?.userName}
                    </span>
                )}
            </header>
            {showNotification && (
                <PreferenceSaveNotification
                    handleClosePreference={handleClosePreference}
                />
            )}
            <Sidebar
                open={sidebarOpen}
                closeSidebar={closeSidebar}
                toggleRef={toggleRef}
            />
            {openHelp && (
                <HelpSection showModal={openHelp} closeHelp={closeHelp} />
            )}
            <WarningPage
                onShow={show}
                onSave={emitEvent}
                onDontSave={closeWarningAndNavigate}
            />
        </>
    );
}

export default Header;
